import { useMemo, useState, useCallback } from 'react';
import { Modal, ModalBody, ModalContent, ModalFooter, ModalHeader } from '@heroui/react';
import { format, parseISO } from 'date-fns';
import {
  ITeacherAttendanceResponse,
  useCreateBulkAttendanceMutation,
} from '@/entities/TeacherAttendance';
import { AppButton } from '@/shared/ui/AppButton';
import { UserAvatar } from '@/shared/ui/UserAvatar';
import { useModalAutoFocus } from '@/shared/lib/hooks';
import {
  LessonAttendanceStudentsTable,
  StudentWithAttendance,
} from './LessonAttendanceStudentsTable';

interface AttendanceCheckModalProps {
  isOpen: boolean;
  onClose: () => void;
  attendanceData?: ITeacherAttendanceResponse;
  groupLessonId?: number | null;
  order: number;
  hasAttendanceSet: boolean;
}

export const LessonAttendanceCheckModal = ({
  isOpen,
  onClose,
  attendanceData,
  groupLessonId,
  order,
  hasAttendanceSet,
}: AttendanceCheckModalProps) => {
  useModalAutoFocus(isOpen);

  const [studentsAttendance, setStudentsAttendance] = useState<Record<number, 0 | 0.5 | 1 | null>>(
    {},
  );
  const [createBulkAttendance, { isLoading: isSaving }] = useCreateBulkAttendanceMutation();

  const students = useMemo(() => {
    if (!attendanceData?.students) return [];

    return attendanceData.students.map((student) => {
      const attendanceValue = student.attendance;
      const normalizedAttendance =
        attendanceValue === 0 || attendanceValue === 0.5 || attendanceValue === 1
          ? attendanceValue
          : null;

      return {
        ...student,
        selectedAttendance: studentsAttendance[student.id] ?? normalizedAttendance,
      } as StudentWithAttendance;
    });
  }, [attendanceData, studentsAttendance]);

  const allStudentsHaveAttendance = useMemo(
    () => students.length > 0 && students.every((student) => student.selectedAttendance !== null),
    [students],
  );

  const handleAttendanceChange = useCallback((studentId: number, value: 0 | 0.5 | 1) => {
    setStudentsAttendance((prev) => ({
      ...prev,
      [studentId]: value,
    }));
  }, []);

  const handleSaveAttendance = async () => {
    if (!groupLessonId || !allStudentsHaveAttendance) return;

    const studentsData = students.map((student) => ({
      enrollmentId: student.id,
      value: student.selectedAttendance!,
    }));

    try {
      await createBulkAttendance({
        groupLessonId,
        students: studentsData,
      }).unwrap();
      onClose();
      setStudentsAttendance({});
    } catch (error) {
      // Error handling: failed to save attendance
    }
  };

  const lesson = attendanceData?.lesson;
  const moduleTeacher = attendanceData?.moduleTeacher;
  const lecturer = attendanceData?.lecturer;

  const scheduledAtFormatted = useMemo(() => {
    if (!lesson?.scheduledAt) return '';
    try {
      return format(parseISO(lesson.scheduledAt), 'dd.MM.yyyy, HH:mm');
    } catch {
      return '';
    }
  }, [lesson]);

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={onClose}
      placement="center"
      size="xl"
      classNames={{ base: 'max-w-[504px] p-9' }}
      isDismissable={false}
    >
      <ModalContent>
        <ModalHeader className="block p-0">
          <h3 className="text-[32px] font-medium mb-5">Attendance Check</h3>
        </ModalHeader>

        <ModalBody className="p-0">
          <div className="mb-5">
            {lesson && (
              <p className="text-grey-secondary text-[18px]">
                Lesson:
                <span className="ml-1 font-medium text-black">
                  {order}. {lesson.title}
                </span>
              </p>
            )}
            {scheduledAtFormatted && (
              <p className="text-grey-secondary text-[14px] text-medium mt-1">
                {scheduledAtFormatted}
              </p>
            )}
            {(moduleTeacher || lecturer) && (
              <div className="flex items-center gap-x-4 mt-2">
                {moduleTeacher && (
                  <UserAvatar
                    avatar={moduleTeacher.avatar}
                    userName={moduleTeacher.username}
                    href={`/staff/${moduleTeacher.id}`}
                  />
                )}
                {lecturer && (
                  <UserAvatar
                    avatar={lecturer.avatar}
                    userName={lecturer.username}
                    href={`/staff/${lecturer.id}`}
                  />
                )}
              </div>
            )}
          </div>
          <div className="max-h-[500px] overflow-y-auto overflow-x-hidden">
            <LessonAttendanceStudentsTable
              students={students}
              onAttendanceChange={handleAttendanceChange}
              isReadOnly={hasAttendanceSet}
            />
          </div>
          {!allStudentsHaveAttendance && students.length > 0 && !hasAttendanceSet && (
            <p className="text-red-500 font-medium">Mark the attendance of all students</p>
          )}
        </ModalBody>

        {!hasAttendanceSet && (
          <ModalFooter className="p-0 mt-6">
            <AppButton
              color="red"
              onPress={handleSaveAttendance}
              isLoading={isSaving}
              disabled={!allStudentsHaveAttendance}
              className="rounded-full w-full"
            >
              Save attendance
            </AppButton>
          </ModalFooter>
        )}
      </ModalContent>
    </Modal>
  );
};
